import { memo, type RefObject } from "react";
import styled from "styled-components";

import { RestoreIcon, SaveIcon } from "../../icons/glyphs";
import { AnnotationTargetStrip } from "../AnnotationTargetStrip";
import { IconButton } from "../IconButton";
import { Spinner } from "../Spinner";
import { PanelSection } from "./AnnotationPanelLayout";
import { hasAnnotationTargetContent } from "./annotationTargetContent";
import { annotationStudioText } from "./messages";
import type { AnnotationFrameDraft, AnnotationStudioLocale, AnnotationTarget } from "./types";

export interface AnnotationToolsPanelProps {
  readonly locale: AnnotationStudioLocale;
  readonly authoring: boolean;
  readonly compact: boolean;
  readonly contentId: string;
  readonly contentRef: RefObject<HTMLElement | null>;
  readonly interactionDisabled: boolean;
  readonly frame: AnnotationFrameDraft;
  readonly targets: readonly AnnotationTarget[];
  readonly selectedTargetId: string | null;
  readonly targetNames: ReadonlyMap<string, string>;
  readonly selectedOnly: boolean;
  readonly visibleTargetIds: readonly string[];
  readonly dirtyTargetIds: ReadonlySet<string>;
  readonly pendingTargetIds: ReadonlySet<string>;
  readonly discardDisabled: boolean;
  readonly saveDisabled: boolean;
  readonly authoringDisabledReasonId?: string;
  readonly savePending: boolean;
  readonly onSelectedOnlyChange: (selectedOnly: boolean) => void;
  readonly onVisibleTargetIdsChange: (targetIds: readonly string[]) => void;
  readonly onTargetChange: (targetId: string) => void;
  readonly onClearTarget: (targetId: string) => void;
  readonly onRequestClearAllTargets: () => void;
  readonly onTargetNameChange: (targetId: string, name: string) => void;
  readonly onRequestDiscardChanges: () => void;
  readonly onSave: () => void;
}

const ToolsLayout = styled.div`
  display: contents;

  & > section {
    gap: 0.3rem;
    padding-top: 0.4rem;
  }
`;

const InspectorTargetStrip = styled(AnnotationTargetStrip)`
  gap: 0.35rem;
`;

const ToolsSection = styled(PanelSection)`
  flex: 0 0 auto;
`;

const TargetPanelFooter = styled.div`
  display: grid;
  min-width: 0;
  /* 보조 동작은 자기 라벨이 요구하는 만큼만 갖고 주 동작이 나머지를 흡수한다. 비율로 나누면 열
     폭이 라벨을 보지 않아, 라벨이 들어갈 폭인데도 접힌다. */
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.35rem;
  padding-top: 0.45rem;

  & > button {
    width: 100%;
    /* 라벨이 접히면 버튼 높이가 조용히 두 배가 되고 그만큼이 아래 섹션 예산에서 빠진다.
       접지 않는다 — 예산이 모자라면 잘림으로 드러나 테스트가 잡는다. */
    white-space: nowrap;
  }
`;

function AnnotationToolsPanelComponent({
  locale,
  authoring,
  compact,
  contentId,
  contentRef,
  interactionDisabled,
  frame,
  targets,
  selectedTargetId,
  targetNames,
  selectedOnly,
  visibleTargetIds,
  dirtyTargetIds,
  pendingTargetIds,
  discardDisabled,
  saveDisabled,
  authoringDisabledReasonId,
  savePending,
  onSelectedOnlyChange,
  onVisibleTargetIdsChange,
  onTargetChange,
  onClearTarget,
  onRequestClearAllTargets,
  onTargetNameChange,
  onRequestDiscardChanges,
  onSave,
}: AnnotationToolsPanelProps) {
  return (
    <ToolsLayout>
      <ToolsSection
        ref={contentRef}
        data-annotation-tools-content=""
        aria-label={annotationStudioText("annotationTargets", locale)}
      >
        <div id={contentId}>
          <InspectorTargetStrip
            targets={targets.map((target) => {
              const targetDraft = frame.targets[target.id];
              return {
                id: target.id,
                name: targetNames.get(target.id),
                color: target.color,
                occupied: targetDraft ? hasAnnotationTargetContent(targetDraft) : false,
                dirty: dirtyTargetIds.has(target.id),
                pending: pendingTargetIds.has(target.id),
                includeCount:
                  targetDraft?.points.filter((point) => point.kind === "include").length ?? 0,
                excludeCount:
                  targetDraft?.points.filter((point) => point.kind === "exclude").length ?? 0,
              };
            })}
            selectedTargetId={selectedTargetId}
            selectedOnly={selectedOnly}
            visibleTargetIds={visibleTargetIds}
            presentation={compact ? "palette" : "list"}
            onSelectedOnlyChange={onSelectedOnlyChange}
            onVisibleTargetIdsChange={onVisibleTargetIdsChange}
            onTargetChange={onTargetChange}
            onClearTarget={onClearTarget}
            onClearAllTargets={onRequestClearAllTargets}
            onTargetNameChange={onTargetNameChange}
            readOnly={!authoring}
            disabled={interactionDisabled}
            activeLabel={annotationStudioText("annotationTargets", locale)}
            annotationLabel={annotationStudioText("objectSlot", locale)}
            showSelectedOnlyLabel={annotationStudioText("showSelectedTargetOnly", locale)}
            showAllTargetsLabel={annotationStudioText("showAllTargets", locale)}
            hideAllTargetsLabel={annotationStudioText("hideAllTargets", locale)}
            showTargetLabel={annotationStudioText("showTarget", locale)}
            hideTargetLabel={annotationStudioText("hideTarget", locale)}
            clearTargetLabel={annotationStudioText("clearTargetAnnotation", locale)}
            clearAllTargetsLabel={annotationStudioText("clearAllTargets", locale)}
            renameTargetLabel={annotationStudioText("renameTarget", locale)}
            unsavedChangesLabel={annotationStudioText("unsaved", locale)}
            positiveMarkerLabel={annotationStudioText("positiveMarker", locale)}
            negativeMarkerLabel={annotationStudioText("negativeMarker", locale)}
            pendingLabel={annotationStudioText("segmenting", locale)}
            occupiedLabel={annotationStudioText("occupiedAnnotation", locale)}
            emptyLabel={annotationStudioText("emptyAnnotation", locale)}
          />
        </div>
        {authoring ? (
          <TargetPanelFooter>
            <IconButton
              size="sm"
              variant="ghost"
              border
              tone="danger"
              text={annotationStudioText("discardChanges", locale)}
              disabled={discardDisabled}
              onClick={onRequestDiscardChanges}
            >
              <RestoreIcon />
            </IconButton>
            <IconButton
              size="sm"
              variant="solid"
              text={
                savePending
                  ? annotationStudioText("saving", locale)
                  : annotationStudioText("save", locale)
              }
              disabled={saveDisabled}
              aria-describedby={saveDisabled ? authoringDisabledReasonId : undefined}
              onClick={onSave}
            >
              {savePending ? (
                <Spinner label={annotationStudioText("saving", locale)} size="sm" />
              ) : (
                <SaveIcon />
              )}
            </IconButton>
          </TargetPanelFooter>
        ) : null}
      </ToolsSection>
    </ToolsLayout>
  );
}

export const AnnotationToolsPanel = memo(AnnotationToolsPanelComponent);
